import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { toastErrorWithHelp } from "@/lib/toastErrorWithHelp";

import { getEngine } from "@/lib/engineSingleton";

import SingOverItCTA from "@/components/SingOverItCTA";
import MidiClipEditor from "@/components/MidiClipEditor";
import ArrangementView from "@/components/ArrangementView";
import RecordingStopBanner from "@/components/RecordingStopBanner";
import StemRoutingPreview from "@/components/daw/StemRoutingPreview";
import PublishStemPackModal from "@/components/marketplace/PublishStemPackModal";
import useRemixPack from "@/components/daw/useRemixPack";
import useStudioIntent from "@/components/daw/useStudioIntent";
import StudioSidebar from "@/components/daw/StudioSidebar";
import StudioHeader from "@/components/daw/StudioHeader";
import CollabPresenceStrip from "@/components/daw/CollabPresenceStrip";
import DAWRightRail from "@/components/daw/DAWRightRail";
import { applyStackWidth } from "@/lib/stackWidth";
import { checkMicAvailability, mapMicError } from "@/lib/micCheck";

import {
  TOTAL_CHANNELS, TIMELINE_VISIBLE_LANES,
  clamp, padCh,
} from "@/components/daw/dawHelpers";
import { AmbientBackdrop } from "@/components/daw/Header";
import { FooterBar, BusyOverlay, TransportBar } from "@/components/daw/Transport";
import TimelinePanel from "@/components/daw/TimelinePanel";
import MixerPanel from "@/components/daw/MixerPanel";
import TrackingControlsBar from "@/components/daw/TrackingControlsBar";
import RoutingMatrixModal from "@/components/daw/RoutingMatrixModal";
import ClipHistoryDropdown from "@/components/daw/ClipHistoryDropdown";
import VocalCleanupModal from "@/components/daw/VocalCleanupModal";
import { loadCustomPresets } from "@/components/daw/quickArmPresets";
import useArmRoutingTemplates from "@/lib/useArmRoutingTemplates";
import useAuth from "@/lib/useAuth";
import useQuickArmHandlers from "@/components/daw/useQuickArmHandlers";
import useRecordingHandlers from "@/components/daw/useRecordingHandlers";
import useStemExtraction from "@/components/daw/useStemExtraction";
import useStemDropFlow from "@/components/daw/useStemDropFlow";
import useLiveMeters from "@/components/daw/useLiveMeters";
import useTransportHandlers from "@/components/daw/useTransportHandlers";
import useChannelLoadHandlers from "@/components/daw/useChannelLoadHandlers";
import useSessionPersistence from "@/components/daw/useSessionPersistence";
import useMasteringHandlers from "@/components/daw/useMasteringHandlers";
import useChannelRenderFlows from "@/components/daw/useChannelRenderFlows";
import useDriveCallbackToast from "@/components/drive/useDriveCallbackToast";
import useTakeHistory from "@/lib/useTakeHistory";
import TakeHistoryModal from "@/components/daw/TakeHistoryModal";
import IframeBanner from "@/components/daw/IframeBanner";
import DawModals from "@/components/daw/DawModals";
import CommandPalette from "@/components/CommandPalette";
import usePanicKey from "@/lib/usePanicKey";
import CollabModal, { peerColor } from "@/components/CollabModal";
import CookingStatusBar from "@/components/CookingStatusBar";
import useSessionMetadata from "@/lib/useSessionMetadata";
import useRecordingMode from "@/lib/useRecordingMode";
import useFxState from "@/lib/useFxState";
import useCollabInfra from "@/components/daw/useCollabInfra";

/* ─────────────────────────────  Main App  ───────────────────────────── */
export default function ELPMStudioDAW() {
  // Lazy-init via useState so the AudioEngine constructor runs EXACTLY ONCE
  // across React 18 StrictMode's dev-time double-mount. Singleton-backed so
  // it survives route navigation (/plugins can load into the same channels).
  const [engine] = useState(() => getEngine());
  if (typeof window !== "undefined") {
    window.__elpmEngine = engine;
  }

  const [playing, setPlaying] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(0);
  // Default to 8 visible channels on mobile, 16 elsewhere.
  const [visibleChannels, setVisibleChannels] = useState(() => {
    if (typeof window === "undefined") return 16;
    return window.matchMedia("(max-width: 767px)").matches ? 8 : 16;
  });
  const [maxDur, setMaxDur] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  // iter-216 — "Load song & extract stems in one click" one-shot flag.
  const [pendingStemExtract, setPendingStemExtract] = useState(false);
  const [recordingIdx, setRecordingIdx] = useState(null);
  // Recording mode (DRY / MONITOR / PRINT) — hook handles engine sync + persistence.
  const [recordingMode, setRecordingMode] = useRecordingMode(engine);
  const [masterVol, setMasterVol] = useState(82);
  const [ceiling, setCeiling] = useState(91);
  const {
    activeEffects,
    fxParamsByChannel,
    fxCounts,
    toggleEffectOnChannel,
    handleFxParamChange: rawFxParamChange,
    handleLoadFxPreset: rawLoadFxPreset,
    // Automation extensions (iter 32)
    automationArmedByChannel,
    automationSummaries,
    toggleAutomationArm,
    clearChannelAutomation,
  } = useFxState(engine);
  const [busy, setBusy] = useState(null);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  // iter-224 — Vocal Cleanup modal. `null` = closed; else `{ ch }`.
  const [cleanupTarget, setCleanupTarget] = useState(null);
  const [masteringStats, setMasteringStats] = useState(null);
  const [masteringTarget, setMasteringTarget] = useState("streaming");
  const [songOpen, setSongOpen] = useState(false);
  const [songSeed, setSongSeed] = useState({ prompt: "", lyrics: "" });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceCount, setVoiceCount] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [midiOpen, setMidiOpen] = useState(false);
  const [recordingsOpen, setRecordingsOpen] = useState(false);
  const [shareSessionOpen, setShareSessionOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);

  /* ─── Real-time collab ─── (iter 33 · extracted into useCollabInfra iter-268)
   *   roomId & userName drive the WebSocket connection.
   *   URL param `?collab=XYZ` auto-joins so shared invite links work.
   *
   *   NOTE: This is metadata-only sync — no audio media streams between peers.
   *   Each browser plays its own local mix; we just keep the controls in sync.
   *   All wiring for the four layered hooks (WebSocket / WebRTC mesh / Yjs
   *   CRDT / channel locks) plus the engine↔Y.Doc broadcast bridge lives in
   *   `useCollabInfra` — 300+ lines of infra collapsed to one call site. */
  const {
    collab,
    mesh,
    yjs,
    channelLocks,
    peerCursors,
    collabRoomId,
    setCollabRoomId,
    collabUserName,
    setCollabUserName,
  } = useCollabInfra({ engine, selectedChannel, setPlaying });
  const [drumOpen, setDrumOpen] = useState(false);
  const [bassOpen, setBassOpen] = useState(false);
  // iter-144 — JSFX editor modal (REAPER-style text DSP, offline render).
  const [jsfxOpen, setJsfxOpen] = useState(false);
  const [backingVocalsOpen, setBackingVocalsOpen] = useState(false);
  const [midiClipOpen, setMidiClipOpen] = useState(false);
  const [arrangementOpen, setArrangementOpen] = useState(false);
  const [clipOpen, setClipOpen] = useState(false);
  const [mtEditorOpen, setMtEditorOpen] = useState(false);
  const [loopsOpen, setLoopsOpen] = useState(false);
  const [jamWizardOpen, setJamWizardOpen] = useState(false);
  const [sunoOpen, setSunoOpen] = useState(false);
  const [loopStationOpen, setLoopStationOpen] = useState(false);
  // Optional prefill passed from Lyrics Writer "Send to Suno → sing them"
  // Shape: { lyrics, style, title } — Suno modal consumes & clears it.
  const [sunoSeed, setSunoSeed] = useState(null);
  // Master width control for the stacked-harmony channels — 0 = mono, 1 = full spread.
  const [stackWidth, setStackWidth] = useState(1);
  // Sing-Over-It CTA — shown after a successful Jam Mode load so the user
  // can jump from "instant backing track" to "vocal performance" in one click.
  const [singCtaInfo, setSingCtaInfo] = useState(null); // { genre, tempo } or null
  // Mixer layout — "strips" (vertical channel strips scrolling horizontally — the original)
  //  or "rows" (each channel is a full-width horizontal row with a left-to-right volume fader).
  const [mixerLayout, setMixerLayout] = useState(() => {
    return (typeof window !== "undefined" ? localStorage.getItem("elpm-mixer-layout") : null) || "rows";
  });
  const [loopEnabled, setLoopEnabled] = useState(false);
  // A↔B loop range — persisted across toggle so producers can flip Loop on/off
  // without losing the region they were practising over. Defaults to full mix
  // once audio is loaded (see effect below).
  const [loopRange, setLoopRange] = useState({ start: 0, end: 0 });
  // Session metadata — tempo, key, dry mode, metronome — extracted into a
  // hook that handles engine wiring + localStorage persistence.
  const {
    tempo, setTempo,
    musicalKey,
    dryMode,
    metronomeOn,
    handleTempoChange,
    handleKeyChange,
    handleToggleDryMode,
    handleToggleMetronome,
  } = useSessionMetadata(engine);

  // iter-105 — Set a CSS custom property `--mixer-pulse-duration` on
  // <html> = 60s / tempo. The mixer-row-bpm-pulse keyframes consume it,
  // so loaded channels visually pulse in time with the current tempo.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const dur = Math.max(0.2, 60 / Math.max(40, tempo || 120));
    document.documentElement.style.setProperty("--mixer-pulse-duration", `${dur}s`);
  }, [tempo]);

  // ─── Global Cmd/Ctrl + K shortcut to open the Command Palette ───
  // Captured at the window level so it fires regardless of focus. We deliberately
  // don't preventDefault on the SET (the browser may have its own Cmd+K binding
  // for tab search) — instead we check for the right modifier combo and only
  // toggle when it's an unambiguous studio-level keypress.
  useEffect(() => {
    const onKey = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (isCtrlOrCmd && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ─── Multi-track recording state ───
  // armedChannels: Set<channelIdx> of channels armed for the next tracking session
  // sourceMap: { [channelIdx]: inputStreamChannel } — which input ch feeds which DAW ch
  // inputDeviceId / inputChannelCount: selected audio interface metadata
  // tracking: boolean — whether a tracking session is currently active
  const [armedChannels, setArmedChannels] = useState(() => new Set());
  const [sourceMap, setSourceMap] = useState({});
  const [inputDeviceId, setInputDeviceId] = useState(null);
  const [inputChannelCount, setInputChannelCount] = useState(0);
  const [tracking, setTracking] = useState(false);
  // Stream-to-disk mode — every MediaRecorder chunk is POSTed to the backend
  // so long sessions don't fill browser memory. Persisted across reloads.
  const [streamToDisk, setStreamToDisk] = useState(() => {
    return (typeof window !== "undefined" ? localStorage.getItem("elpm-stream-to-disk") : null) === "1";
  });
  const [streamSessionId, setStreamSessionId] = useState(null);
  const [streamBytesUploaded, setStreamBytesUploaded] = useState(0);
  // iter-204 — Routing Matrix modal (input → DAW channel map). Session-only,
  // no persistence per user spec.
  const [routingMatrixOpen, setRoutingMatrixOpen] = useState(false);
  // iter-207 — User's personally-saved arm/routing presets (persisted in
  // localStorage). Loaded lazily and refreshed whenever the user adds /
  // removes a preset. Lives in React state so the Quick Arm row can
  // re-render without forcing a parent diff.
  const [customArmPresets, setCustomArmPresets] = useState(() => loadCustomPresets());

  // Refresh voice count from backend on mount and whenever the modal closes (after add/delete).
  useEffect(() => {
    if (voiceOpen) return; // skip while modal open — it fetches its own list
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/list`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => { if (!cancelled) setVoiceCount(Array.isArray(data) ? data.length : 0); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [voiceOpen]);

  const channels = engine.channels;

  // Track-list live meters (peaks / playhead / clip detection / live waves).
  const {
    peaks,
    masterPeak,
    playhead,
    setPlayhead,
    recordingMeters,
    liveWaveRev,
    clipCount,
    clipHistoryRef,
    clipHistoryRevRef,
    clipHistoryOpen,
    setClipHistoryOpen,
    clipHistoryRev,
    setClipHistoryRev,
    isClipFlashing,
    isClipBadgeActive,
    readLiveSamples,
    channelVolumes,
  } = useLiveMeters({ engine, channels, recordingIdx, setPlaying, setStreamBytesUploaded });

  const playheadPct = maxDur > 0 ? clamp((playhead / maxDur) * 100, 0, 100) : 0;

  /* Transport + loop + sync handlers */
  const {
    handlePlay,
    handleStop,
    handleToggleLoop,
    handleLoopRangeChange,
    handleLoopReset,
    handleSynced,
    handleQuantized,
    handleSyncCleared,
    handleLoadedClick,
    handleLengthClick,
  } = useTransportHandlers({
    engine,
    playing,
    setPlaying,
    loadedCount,
    setPlayhead,
    maxDur,
    setMaxDur,
    masterVol,
    setMasterVol,
    loopEnabled,
    setLoopEnabled,
    loopRange,
    setLoopRange,
    visibleChannels,
    setVisibleChannels,
    setSelectedChannel,
  });

  // iter-158 — Forward ref to handleRecord (defined further down). Lets the
  // panic-key hook call it without hoisting the whole function.
  const handleRecordRef = useRef(null);

  // iter-158 — Cmd/Ctrl + . (panic) universal "stop everything" shortcut.
  const isAnyRecording = recordingIdx !== null || tracking;
  usePanicKey({
    isRecording: isAnyRecording,
    stopRecording: () => { handleRecordRef.current?.(); },
    playing,
    stopPlayback: handleStop,
    onPanic: (stoppedCount) => {
      if (stoppedCount > 0) {
        toast.message("Panic · everything stopped", {
          description: `Stopped ${stoppedCount} active transport${stoppedCount === 1 ? "" : "s"}. (Cmd/Ctrl + . is the industry-standard panic shortcut.)`,
          duration: 3500,
        });
      }
    },
  });

  /* Jam Mode + Vocal Cleanup apply */
  const { busyJam, applyVocalCleanup } = useChannelRenderFlows({
    engine, setPlaying, setTempo, setMaxDur, setLoadedCount, setSingCtaInfo,
  });

  // iter-112 — listen for `elpm:jump-to-channel` events fired from
  // modal/toast surfaces (e.g. backing-vocals "Open in mixer" action) so
  // they can scroll the mixer to a specific row without prop-drilling
  // setSelectedChannel + setVisibleChannels through every parent layer.
  useEffect(() => {
    const onJump = (e) => {
      const ch = e?.detail?.ch;
      if (typeof ch !== "number" || ch < 0 || ch >= TOTAL_CHANNELS) return;
      setSelectedChannel(ch);
      if (ch >= visibleChannels) {
        setVisibleChannels(clamp(ch + 8, 8, TOTAL_CHANNELS));
      }
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="mixer-row-${ch}"]`)
                || document.querySelector(`[data-testid="channel-strip-${ch}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      });
    };
    window.addEventListener("elpm:jump-to-channel", onJump);
    return () => window.removeEventListener("elpm:jump-to-channel", onJump);
  }, [visibleChannels]);

  // iter-139 — listen for `elpm:open-drum-machine` events fired by the
  // SharedKitImporter when a `?kit=` URL lands. Opens the modal so the
  // user can see their imported kit immediately.
  useEffect(() => {
    const onOpenDrum = () => setDrumOpen(true);
    const onOpenBass = () => setBassOpen(true);
    const onOpenJsfx = () => setJsfxOpen(true);
    window.addEventListener("elpm:open-drum-machine", onOpenDrum);
    window.addEventListener("elpm:open-bass-player",  onOpenBass);
    window.addEventListener("elpm:open-jsfx",         onOpenJsfx);
    return () => {
      window.removeEventListener("elpm:open-drum-machine", onOpenDrum);
      window.removeEventListener("elpm:open-bass-player",  onOpenBass);
      window.removeEventListener("elpm:open-jsfx",         onOpenJsfx);
    };
  }, []);


  /* ─── Recording handlers (transport + per-row) ───
     iter-245: extracted into `useRecordingHandlers` to slim the parent
     by ~330 lines. Behaviour byte-identical — pure cut/paste. The
     iter-158 `handleRecordRef` pattern is preserved (panic-key hook
     declared up top still calls into the latest closure). */
  // iter-246 — Per-channel take history (in-memory ring buffer, 5 takes).
  const takeHistory = useTakeHistory({ maxPerChannel: 5 });
  const [takeHistoryChannel, setTakeHistoryChannel] = useState(null); // ch idx when modal open

  const { handleRecord, handleRecordChannel } = useRecordingHandlers({
    engine,
    tracking,
    setTracking,
    recordingIdx,
    setRecordingIdx,
    setMaxDur,
    setLoadedCount,
    armedChannels,
    setArmedChannels,
    setStreamSessionId,
    setStreamBytesUploaded,
    streamToDisk,
    inputDeviceId,
    sourceMap,
    selectedChannel,
    setSelectedChannel,
    checkMicAvailability,
    mapMicError,
    toastErrorWithHelp,
    pushTakeToHistory: takeHistory.pushTake,
  });
  // iter-158 — Keep the ref pointing at the latest handleRecord closure so
  // the panic-key hook (declared up top, before handleRecord exists) can
  // still reach the current version with all its captured state.
  handleRecordRef.current = handleRecord;

  // iter-246 — handlers wired through to the modal for revert / delete.
  const handleTakeRevert = (takeId) => {
    if (takeHistoryChannel === null) return;
    const reverted = takeHistory.revertTake(takeHistoryChannel, takeId, engine);
    if (reverted) {
      // Trigger UI re-render so MixerRow picks up the swapped buffer.
      setLoadedCount(engine.channels.filter((c) => c.buffer).length);
      setMaxDur(engine.maxDuration);
      toast.success(`Reverted CH ${padCh(takeHistoryChannel)} → "${reverted.label}"`, {
        description: `${reverted.durationSec.toFixed(2)}s · current buffer was pushed back into history (undoable).`,
        duration: 5500,
      });
    }
    setTakeHistoryChannel(null);
  };
  const handleTakeDelete = (takeId) => {
    if (takeHistoryChannel === null) return;
    // We don't have a per-take delete in the hook; emulate by reading the
    // current list, filtering, and re-pushing what we keep. Quick + cheap.
    const list = takeHistory.takesByChannel[takeHistoryChannel] || [];
    const survivor = list.filter((t) => t.id !== takeId);
    takeHistory.clearChannel(takeHistoryChannel);
    survivor.forEach((t) => takeHistory.pushTake(takeHistoryChannel, t.buffer, t.label));
    toast.message("Take removed from history");
  };

  /* ─── Multi-track arm / disarm + Quick Arm preset handlers ───
     iter-244: extracted into `useQuickArmHandlers` to slim ELPMStudioDAW.jsx
     by ~235 lines. Behaviour byte-identical — just relocated. */
  const { user: authUser, signIn: authSignIn } = useAuth();
  const myUserId = authUser?.user_id || null;
  const arTpl = useArmRoutingTemplates();
  const {
    handleToggleArm,
    handleArmAll,
    handleDisarmAll,
    handleQuickArmTemplate,
    handleSaveCustomArmPreset,
    handleApplyCustomArmPreset,
    handleRemoveCustomArmPreset,
    handleShareCustomArmPreset,
    handleApplyCommunityArmPreset,
    handleToggleCommunityLike,
    handleDeleteCommunityPreset,
    handleSourceChChange,
    handleAutoBalanceRouting,
  } = useQuickArmHandlers({
    armedChannels,
    setArmedChannels,
    sourceMap,
    setSourceMap,
    visibleChannels,
    inputChannelCount,
    setCustomArmPresets,
    setRoutingMatrixOpen,
    arTpl,
    authUser,
  });

  const handleInputDeviceChange = (id, channelCount) => {
    setInputDeviceId(id);
    setInputChannelCount(channelCount || 0);
    engine.setInputDevice(id);
    toast.message(`Input device set`, { description: channelCount > 1 ? `${channelCount}-channel interface ready for multi-track tracking.` : "Mono input ready." });
  };
  const handleToggleStreamToDisk = (next) => {
    setStreamToDisk(next);
    try { localStorage.setItem("elpm-stream-to-disk", next ? "1" : "0"); } catch (e) { void e; }
    toast.message(next ? "Stream to disk ON" : "Stream to disk OFF", {
      description: next
        ? "Every chunk uploads in real-time. Browser memory stays constant — record for hours."
        : "Recording is held in browser memory until you stop. Best for short takes.",
    });
  };

  /* Channel load / drop / clear + header upload */
  const {
    handleUpload,
    handleDropOnChannel,
    handleDropOnTimeline,
    handleClear,
    headerUploadRef,
    handleHeaderUploadClick,
    handleHeaderFilePicked,
  } = useChannelLoadHandlers({
    engine, setBusy, setMaxDur, setLoadedCount, selectedChannel, setSelectedChannel,
  });

  /* Session save / load / new project */
  const { handleSave, handleNewProject, handleLoadSession } = useSessionPersistence({
    engine,
    masterVol, setMasterVol,
    ceiling, setCeiling,
    tempo, setTempo,
    musicalKey, handleKeyChange,
    activeEffects, fxParamsByChannel, toggleEffectOnChannel,
    setLoadedCount, setMaxDur, setLoopEnabled, setLoopRange, setSelectedChannel,
  });

  /* Master bus — export / mastering */
  const { handleExport, handleMaster, handleMasteringReset, handleMasterAndBounce } = useMasteringHandlers({
    engine, loadedCount, maxDur, setBusy, masteringTarget, setMasteringStats,
  });

  // iter-251 — Stem-extraction pipeline (handleStemExtract,
  // runStemExtraction, handleCancelStems, stemConfirm/stemProgress
  // state) extracted into a dedicated hook. Refactor only — behaviour
  // is byte-identical to the pre-iter-251 inline implementation.
  const {
    stemConfirm,
    setStemConfirm,
    stemProgress,
    handleStemExtract,
    runStemExtraction,
    handleCancelStems,
    lastExtraction,
    downloadStemPack,
  } = useStemExtraction({
    engine,
    selectedChannel,
    setSelectedChannel,
    setBusy,
    setMaxDur,
    setLoadedCount,
    setVisibleChannels,
  });

  // iter-261 — Stems Marketplace publish modal. Opens from the
  // StemExtractorCard's "Publish to marketplace" button (only visible
  // after a successful extraction). The modal itself handles the
  // signed-in vs anon CTA branch.
  const [publishStemPackOpen, setPublishStemPackOpen] = useState(false);

  // iter-278 — StemExtractorCard load/drop flows extracted verbatim
  // into their own hook (was ~100 inline JSX lines).
  const { onStemLoadAudio, onStemDropAudio } = useStemDropFlow({
    engine,
    handleUpload,
    handleStemExtract,
    setSelectedChannel,
    setPendingStemExtract,
    setLibraryOpen,
  });

  // iter-262 — Stems Marketplace "Remix this pack". When the user lands
  // on /studio?remix=<pack_id> we fetch the pack + load every stem URL
  // into the next set of free channels. Idempotent + url-cleaning.
  useRemixPack({
    engine,
    setSelectedChannel,
    setVisibleChannels,
    setLoadedCount,
    setMaxDur,
  });
  // Creator → Pro hand-off: /studio?song=<id>&intent=<record|upload|…>.
  useStudioIntent({
    engine, loadedCount, setLoadedCount, setMaxDur, setSelectedChannel, setVisibleChannels,
    actions: {
      recordings: () => setRecordingsOpen(true),
      session: () => handleLoadSession(),
      upload: () => handleHeaderUploadClick(),
      beat: () => setDrumOpen(true),
      record: (idx) => { if (idx != null) setSelectedChannel(Math.min(TOTAL_CHANNELS - 1, idx + 1)); },
    },
  });
  // Google Drive OAuth round-trip lands on /studio?drive=connected|error.
  useDriveCallbackToast();

  // iter-216 — Auto-fire stem extraction the moment a song lands on a
  // channel IF the user clicked the "Load song & extract stems" CTA.
  // One-shot: the flag clears on fire OR when the user cancels by
  // closing My Songs without loading anything.
  useEffect(() => {
    if (!pendingStemExtract) return;
    if (loadedCount === 0) return;
    // Small delay so the SongLibrary modal close animation completes
    // before the routing-preview modal opens (visual polish).
    const t = setTimeout(() => {
      setPendingStemExtract(false);
      handleStemExtract();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleStemExtract is stable enough
  }, [pendingStemExtract, loadedCount]);
  // iter-216 — Clear the pending flag if the user closes My Songs
  // without actually loading a song. Without this, a stale "pending"
  // could cause an unexpected auto-fire on the *next* song load
  // (e.g. user opens My Songs via the toolbar later).
  useEffect(() => {
    if (!pendingStemExtract) return;
    if (libraryOpen) return; // still open
    // Library closed AND we have pending; check if a song actually loaded.
    // If loadedCount is still 0, user cancelled — clear the flag.
    if (loadedCount === 0) {
      const t = setTimeout(() => setPendingStemExtract(false), 100);
      return () => clearTimeout(t);
    }
  }, [pendingStemExtract, libraryOpen, loadedCount]);


  const toggleEffect = (effectKey) => {
    toggleEffectOnChannel(selectedChannel, effectKey);
  };

  const selectedFx = activeEffects[selectedChannel] || {};
  const selectedFxParams = fxParamsByChannel[selectedChannel] || {};

  // Thin wrappers so the SideCard JSX can keep its (effectKey, paramKey, value)
  // / (preset) signatures. The underlying hook handlers take an explicit
  // channelIdx so we pass selectedChannel here.
  const handleFxParamChange = (effectKey, paramKey, value) => {
    rawFxParamChange(selectedChannel, effectKey, paramKey, value);
  };
  const handleLoadFxPreset = (preset) => {
    rawLoadFxPreset(selectedChannel, preset);
  };

  // ─── Sidechain mirror (iter 34) ────────────────────────────────────
  // We don't put this in useFxState because it's a simple selected-channel
  // value that doesn't need per-channel mirroring like fxParams does.
  // Polling-free: every setSidechain call updates the engine + this state
  // in lockstep, and incoming peer changes flow through here too.
  const [sidechainState, setSidechainState] = useState(() => engine.getSidechain(selectedChannel));
  useEffect(() => {
    // When the user switches channels, refresh the mirror from the engine.
    setSidechainState(engine.getSidechain(selectedChannel));
  }, [selectedChannel, engine]);
  const handleSidechainChange = (patch) => {
    engine.setSidechain(selectedChannel, patch);
    setSidechainState({ ...engine.getSidechain(selectedChannel) });
  };

  // Channels-with-audio list — used by the sidechain trigger dropdown.
  // Refreshes any time loadedCount changes (new audio loaded / removed).
  const channelsWithAudio = useMemo(() => {
    const out = [];
    engine.channels.forEach((c, i) => {
      if (c.buffer) out.push({ idx: i, name: c.name || `Track ${i + 1}` });
    });
    return out;
    // loadedCount as dep so the list refreshes when audio is loaded/removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedCount, engine]);

  // Derived: list of peer cursors with metadata for the presence strip.
  // Only renders entries whose channel is a valid number.
  const peerCursorList = useMemo(() => {
    return Object.entries(peerCursors)
      .map(([id, c]) => ({ id, name: c.name, channel: c.channel }))
      .filter((x) => typeof x.channel === "number" && x.channel >= 0 && x.channel < 64);
  }, [peerCursors]);

  /* Mixer event handlers */
  const handleMixerFewer = () => setVisibleChannels((n) => clamp(n - 8, 8, TOTAL_CHANNELS));
  const handleMixerMore  = () => setVisibleChannels((n) => clamp(n + 8, 8, TOTAL_CHANNELS));
  const handleMasterVolChange = (e) => { const v = Number(e.target.value); setMasterVol(v); engine.setMasterVolume(v); };
  const handleCeilingChange = (e) => { const v = Number(e.target.value); setCeiling(v); engine.setLimiterCeiling(v); };

  const visibleTimelineLanes = Math.min(visibleChannels, TIMELINE_VISIBLE_LANES);

  return (
    <div className="min-h-screen overflow-x-hidden font-body text-white">
      <AmbientBackdrop />
      <IframeBanner />
      {/* iter-148 — Floating "STOP RECORDING" banner. Renders only during a
          take so the user has an unmissable target + spacebar/Esc to stop. */}
      <RecordingStopBanner
        recordingIdx={recordingIdx}
        tracking={tracking}
        onStop={handleRecord}
      />
      {/* iter-167 — Stem routing preview. Shown after the user clicks
          Extract Stems and BEFORE the fal.ai upload kicks off.
          iter-168 — passes the set of occupied channels so the modal can
          warn about overwrites + receives the user's edited routing
          back on confirm. */}
      <StemRoutingPreview
        open={!!stemConfirm}
        sourceIdx={stemConfirm?.sourceIdx ?? 0}
        targets={stemConfirm?.targets || []}
        occupiedChannels={new Set((engine.channels || []).map((c, i) => c?.buffer ? i : -1).filter((i) => i >= 0))}
        onConfirm={(routing) => runStemExtraction(stemConfirm.sourceIdx, routing)}
        onCancel={() => setStemConfirm(null)}
      />
      {/* iter-261 — Publish to Stems Marketplace. Mounted lazily via
          `open` flag. Pulls the latest extraction's stems + manifest
          straight from `lastExtraction` and posts to /publish on
          submit. Anon visitors see a sign-in CTA in the modal. */}
      <PublishStemPackModal
        open={publishStemPackOpen}
        onClose={() => setPublishStemPackOpen(false)}
        lastExtraction={lastExtraction}
        signedIn={!!authUser}
        onSignIn={authSignIn}
        onPublished={() => setPublishStemPackOpen(false)}
      />
      {/* iter-224 — Vocal Cleanup modal. Lazily mounted via `open` flag
          so the framer-motion exit animation runs cleanly. */}
      <VocalCleanupModal
        open={cleanupTarget !== null}
        channelIdx={cleanupTarget ?? 0}
        channelName={cleanupTarget !== null ? engine.channels[cleanupTarget]?.name : ""}
        hasAudio={cleanupTarget !== null ? !!engine.channels[cleanupTarget]?.buffer : false}
        onClose={() => setCleanupTarget(null)}
        onGetSourceBlob={() => (cleanupTarget !== null ? engine.channelToWavBlob(cleanupTarget) : null)}
        onApply={applyVocalCleanup}
      />
      {/* iter-246 — Take History modal. Renders the per-channel ring buffer
          of previous takes with one-click Revert + Delete per entry. */}
      <TakeHistoryModal
        open={takeHistoryChannel !== null}
        channelIdx={takeHistoryChannel}
        channelName={takeHistoryChannel !== null ? engine.channels[takeHistoryChannel]?.name : ""}
        takes={takeHistoryChannel !== null ? takeHistory.takesByChannel[takeHistoryChannel] : []}
        onClose={() => setTakeHistoryChannel(null)}
        onRevert={handleTakeRevert}
        onDeleteTake={handleTakeDelete}
        onClearChannel={() => {
          if (takeHistoryChannel !== null) takeHistory.clearChannel(takeHistoryChannel);
          setTakeHistoryChannel(null);
          toast.message("Take history cleared");
        }}
      />
      {/* iter-111 — Spotify-style "Now cooking" status bar pinned to the
          bottom of the viewport. Renders nothing when no tasks are active. */}
      <CookingStatusBar />

      <div className="relative z-10 mx-auto max-w-[1880px] p-4 md:p-6">
        {/* iter-177 — Left navigation sidebar wraps the existing studio
            content so the layout matches the new design reference. All
            interactive triggers (modal opens, scrollTop) are wired
            through `actions` so the sidebar doesn't need to know about
            internal state. */}
        <div className="flex gap-5">
          <StudioSidebar
            activeId="studio"
            engineStatus={engine.ctx.state === "running" ? "Ready" : engine.ctx.state === "suspended" ? "Suspended" : "Idle"}
            engineCtx={engine.ctx}
            tempo={tempo}
            songKey={musicalKey}
            mode={dryMode ? "Dry" : "Wet"}
            sampleRate={engine.ctx?.sampleRate}
            latencyMs={engine.ctx?.baseLatency ? Math.round(engine.ctx.baseLatency * 1000) : 32}
            actions={{
              scrollTop: () => window.scrollTo({ top: 0, behavior: "smooth" }),
              newProject: handleNewProject,
              openLibrary: () => setLibraryOpen(true),
              openSavedSessions: () => setShareSessionOpen(true),
              openRecordings: () => setRecordingsOpen(true),
              openBackingVocals: () => setBackingVocalsOpen(true),
              openPresets: () => setJsfxOpen(true),
              openTemplates: () => { setSunoOpen(true); setTimeout(() => window.dispatchEvent(new Event("elpm:open-templates")), 600); },
              openPlugins: () => setJsfxOpen(true),
              openAccount: () => window.dispatchEvent(new Event("elpm:open-pricing")),
              openSettings: () => window.dispatchEvent(new Event("elpm:open-settings")),
              openSuno: () => setSunoOpen(true),
              openCompose: () => { setSongSeed({ prompt: "", lyrics: "" }); setSongOpen(true); },
              openLyrics: () => setLyricsOpen(true),
              openJam: () => setJamWizardOpen(true),
              openLoopStation: () => setLoopStationOpen(true),
              openMtEditor: () => setMtEditorOpen(true),
              openDrum: () => setDrumOpen(true),
              openBass: () => setBassOpen(true),
              openLoops: () => setLoopsOpen(true),
              openMidi: () => setMidiOpen(true),
              saveSession: handleSave,
              exportWav: handleExport,
              openShare: () => setShareSessionOpen(true),
            }}
            channels={engine.channels}
            selectedChannel={selectedChannel}
            playing={playing}
            maxDur={maxDur}
            playStartedAt={engine.startTime}
            onJumpToChannel={(idx) => { if (idx >= 0) setSelectedChannel(idx); }}
            onSeek={(seconds) => engine.seekTo(seconds)}
            onTogglePlay={handlePlay}
          />

          <div className="min-w-0 flex-1">
        <StudioHeader
          onUpload={handleHeaderUploadClick}
          onSave={handleSave}
          onExport={handleExport}
          onLyrics={() => setLyricsOpen(true)}
          onSong={() => { setSongSeed({ prompt: "", lyrics: "" }); setSongOpen(true); }}
          onLibrary={() => setLibraryOpen(true)}
          onMidi={() => setMidiOpen(true)}
          onDrum={() => setDrumOpen(true)}
          onBass={() => setBassOpen(true)}
          onJsfx={() => setJsfxOpen(true)}
          onBackingVocals={() => setBackingVocalsOpen(true)}
          onLoops={() => setLoopsOpen(true)}
          onJam={() => setJamWizardOpen(true)}
          onSuno={() => setSunoOpen(true)}
          onLoopStation={() => setLoopStationOpen(true)}
          onMtEditor={() => setMtEditorOpen(true)}
          onRecordings={() => setRecordingsOpen(true)}
          onShare={() => setShareSessionOpen(true)}
          busyJam={busyJam}
          busyExport={busy === "export"}
          headerUploadRef={headerUploadRef}
          onFilePicked={handleHeaderFilePicked}
          collabStatus={collab.status}
          collabPeers={collab.peers}
          onCollabClick={() => setCollabOpen(true)}
          onCommandPaletteOpen={() => setCommandPaletteOpen(true)}
          engineState={engine.ctx.state}
          playing={playing}
          loadedCount={loadedCount}
          maxDur={maxDur}
          recordingIdx={recordingIdx}
          tempo={tempo}
          musicalKey={musicalKey}
          dryMode={dryMode}
          metronomeOn={metronomeOn}
          onEngineClick={handlePlay}
          onTempoChange={handleTempoChange}
          onKeyChange={handleKeyChange}
          onLoadedClick={handleLoadedClick}
          onLengthClick={handleLengthClick}
          onRecClick={handleRecord}
          onToggleDryMode={handleToggleDryMode}
          onToggleMetronome={handleToggleMetronome}
          clipCount={clipCount}
          onClipPillClick={() => {
            // iter-219 — Clip History Dropdown — snapshot the rolling
            // ref into React state on open so the dropdown renders
            // deterministically without polling the ref.
            setClipHistoryRev(clipHistoryRevRef.current);
            setClipHistoryOpen((v) => !v);
          }}
        />
        <ClipHistoryDropdown
          open={clipHistoryOpen}
          onClose={() => setClipHistoryOpen(false)}
          history={clipHistoryRef.current}
          channelNames={channels.map((c) => c?.name)}
          onJumpTo={(at) => {
            try { engine.setPlayhead?.(at); } catch (e) { void e; }
            setPlayhead(at);
            setClipHistoryOpen(false);
            toast.message(`Jumped to ${Math.floor(at / 60)}:${String(Math.floor(at % 60)).padStart(2, "0")}`, {
              description: "Press play to hear the clip in context.",
              duration: 4000,
            });
          }}
          onClear={() => {
            clipHistoryRef.current = [];
            clipHistoryRevRef.current++;
            setClipHistoryRev(clipHistoryRevRef.current);
            setClipHistoryOpen(false);
            toast.message("Clip history cleared");
          }}
          /* clipHistoryRev forces a re-render when push() happens — keep
             the prop so React picks up the rev change for memo bail-outs. */
          key={`clip-history-rev-${clipHistoryRev}`}
        />

        <main className="grid gap-4 xl:grid-cols-[1fr_400px]">
          <section className="min-w-0 space-y-4">
            <TimelinePanel
              channels={channels}
              visibleTimelineLanes={visibleTimelineLanes}
              selectedChannel={selectedChannel}
              playheadPct={playheadPct}
              maxDur={maxDur}
              recordingIdx={recordingIdx}
              recordingMeters={recordingMeters}
              onDropFile={handleDropOnChannel}
              onDropFiles={handleDropOnTimeline}
              onSelectChannel={setSelectedChannel}
              loopEnabled={loopEnabled}
              loopStart={loopRange.start}
              loopEnd={loopRange.end}
              onLoopRangeChange={handleLoopRangeChange}
              onLoopReset={handleLoopReset}
              onOpenClipEditor={() => setClipOpen(true)}
              engine={engine}
              loadedCount={loadedCount}
              tempo={tempo}
              onSynced={handleSynced}
              onQuantized={handleQuantized}
              onCleared={handleSyncCleared}
              playing={playing}
              readLiveSamples={readLiveSamples}
              isClipFlashing={isClipFlashing}
              isClipBadgeActive={isClipBadgeActive}
              liveWaveRev={liveWaveRev}
            />

            {/* ─── Collab presence strip (iter-178: extracted to its own
                component for legibility). Renders nothing in solo sessions
                or when no peers are present. */}
            <CollabPresenceStrip
              peers={peerCursorList}
              status={collab.status}
              peerColor={peerColor}
              onSelectChannel={setSelectedChannel}
            />

            <MixerPanel
              channels={channels}
              visibleChannels={visibleChannels}
              selectedChannel={selectedChannel}
              peaks={peaks}
              channelVolumes={channelVolumes}
              engine={engine}
              recordingIdx={recordingIdx}
              fxCounts={fxCounts}
              activeEffects={activeEffects}
              onToggleFx={toggleEffectOnChannel}
              onSelectChannel={setSelectedChannel}
              onUpload={handleUpload}
              onClear={handleClear}
              onFewer={handleMixerFewer}
              onMore={handleMixerMore}
              playing={playing}
              layout={mixerLayout}
              onLayoutChange={(l) => {
                setMixerLayout(l);
                try { localStorage.setItem("elpm-mixer-layout", l); } catch (e) { void e; }
              }}
              onOpenClipEditor={() => setClipOpen(true)}
              stackWidth={stackWidth}
              onStackWidthChange={(w) => {
                setStackWidth(w);
                applyStackWidth(engine, w);
              }}
              armedChannels={armedChannels}
              onToggleArm={handleToggleArm}
              onRecordChannel={handleRecordChannel}
              onOpenVocalCleanup={(ch) => setCleanupTarget(ch)}
              sourceMap={sourceMap}
              onSourceChChange={handleSourceChChange}
              inputChannelCount={inputChannelCount}
              channelLocks={channelLocks}
              collabConnected={collab.status === "connected"}
              readLiveSamples={readLiveSamples}
              isClipFlashing={isClipFlashing}
              isClipBadgeActive={isClipBadgeActive}
              liveWaveRev={liveWaveRev}
              takeCounts={Object.fromEntries(
                Object.entries(takeHistory.takesByChannel).map(([ch, list]) => [ch, list.length])
              )}
              onOpenTakeHistory={(ch) => setTakeHistoryChannel(ch)}
              loopLengthSec={loopEnabled ? Math.max(0, loopRange.end - loopRange.start) : 0}
            />
            <TrackingControlsBar
              armedCount={armedChannels.size}
              tracking={tracking}
              inputDeviceId={inputDeviceId}
              onInputDeviceChange={handleInputDeviceChange}
              onArmAll={handleArmAll}
              onDisarmAll={handleDisarmAll}
              onRecord={handleRecord}
              streamToDisk={streamToDisk}
              onToggleStreamToDisk={handleToggleStreamToDisk}
              sessionId={streamSessionId}
              bytesStreamed={streamBytesUploaded}
              onOpenRoutingMatrix={() => setRoutingMatrixOpen(true)}
              onQuickArmTemplate={handleQuickArmTemplate}
              customArmPresets={customArmPresets}
              onSaveCustomArmPreset={handleSaveCustomArmPreset}
              onApplyCustomArmPreset={handleApplyCustomArmPreset}
              onRemoveCustomArmPreset={handleRemoveCustomArmPreset}
              onSharePreset={handleShareCustomArmPreset}
              communityTemplates={arTpl.templates}
              communityLoading={arTpl.loading}
              communityError={arTpl.error}
              communitySort={arTpl.sort}
              setCommunitySort={arTpl.setSort}
              communityCategory={arTpl.category}
              setCommunityCategory={arTpl.setCategory}
              myUserId={myUserId}
              onApplyCommunityPreset={handleApplyCommunityArmPreset}
              onToggleCommunityLike={handleToggleCommunityLike}
              onDeleteCommunityPreset={handleDeleteCommunityPreset}
            />
            <RoutingMatrixModal
              open={routingMatrixOpen}
              onClose={() => setRoutingMatrixOpen(false)}
              armedChannels={armedChannels}
              sourceMap={sourceMap}
              channelNames={channels.map((c) => c?.name)}
              inputChannelCount={inputChannelCount || 1}
              onSourceChange={handleSourceChChange}
              onAutoBalance={handleAutoBalanceRouting}
            />
          </section>

          <DAWRightRail
            masterVol={masterVol}
            ceiling={ceiling}
            masterPeak={masterPeak}
            onVolChange={handleMasterVolChange}
            onCeilingChange={handleCeilingChange}
            onMaster={handleMaster}
            onMasterAndBounce={handleMasterAndBounce}
            masterBusy={busy === "master" ? "mastering" : busy === "master-and-bounce" ? "master-and-bounce" : false}
            masteringStats={masteringStats}
            masteringTarget={masteringTarget}
            onTargetChange={setMasteringTarget}
            onMasteringReset={handleMasteringReset}
            onVoiceOpen={() => setVoiceOpen(true)}
            voiceCount={voiceCount}
            selectedChannel={selectedChannel}
            selectedChannelHasAudio={!!engine.channels?.[selectedChannel]?.buffer}
            firstChannelWithAudio={engine.channels?.findIndex?.((c) => c?.buffer) ?? -1}
            onStemExtract={handleStemExtract}
            onStemLoadAudio={onStemLoadAudio}
            onStemDropAudio={onStemDropAudio}
            stemBusy={busy === "stems"}
            lastExtractionCount={lastExtraction?.stems?.length || 0}
            lastExtractionSongName={lastExtraction?.songName || ""}
            onDownloadStemPack={() => downloadStemPack()}
            onPublishStemPack={() => setPublishStemPackOpen(true)}
            selectedFx={selectedFx}
            fxParams={selectedFxParams}
            onToggleFx={toggleEffect}
            onParamChange={handleFxParamChange}
            onLoadPreset={handleLoadFxPreset}
            automationArmed={!!automationArmedByChannel[selectedChannel]}
            automationSummary={automationSummaries[selectedChannel] || engine.getChannelAutomationSummary(selectedChannel)}
            automationLanes={engine.getChannelAutomationLanes(selectedChannel)}
            enginePlaying={playing}
            onToggleAutomationArm={() => toggleAutomationArm(selectedChannel)}
            onClearAutomation={() => clearChannelAutomation(selectedChannel)}
            onClearLaneParam={(name, key) => clearChannelAutomation(selectedChannel, name, key)}
            sidechainState={sidechainState}
            channelsWithAudio={channelsWithAudio}
            onSidechainChange={handleSidechainChange}
          />
        </main>

        <FooterBar sampleRate={engine.ctx.sampleRate} baseLatency={engine.ctx.baseLatency} />
        {/* Spacer to prevent the sticky TransportBar from covering the FooterBar. */}
        <div aria-hidden="true" className="h-24" />
          </div>{/* /min-w-0 flex-1 — iter-177 main content column */}
        </div>{/* /flex gap-5 — iter-177 sidebar row */}
      </div>

      <TransportBar
        playing={playing}
        onPlay={handlePlay}
        onStop={handleStop}
        onRecord={handleRecord}
        recordingIdx={recordingIdx}
        tracking={tracking}
        playhead={playhead}
        maxDur={maxDur}
        loopEnabled={loopEnabled}
        onToggleLoop={handleToggleLoop}
        recordingMode={recordingMode}
        onRecordingModeChange={setRecordingMode}
      />

      <BusyOverlay
        busy={busy}
        selectedChannel={selectedChannel}
        stemProgress={stemProgress}
        onCancelStems={busy === "stems" ? handleCancelStems : null}
      />
      {/* All heavy modals are lazy-loaded — wrap in a single Suspense boundary
          so a chunk that's still in-flight doesn't blank the whole app. The
          fallback is intentionally `null` because modals start closed; users
          never see a loading state for an unopened modal. */}
      <DawModals
        engine={engine}
        channels={channels}
        selectedChannel={selectedChannel}
        playhead={playhead}
        setSelectedChannel={setSelectedChannel}
        setMaxDur={setMaxDur}
        setLoadedCount={setLoadedCount}
        setPlaying={setPlaying}
        setTempo={setTempo}
        setSunoSeed={setSunoSeed}
        setSongSeed={setSongSeed}
        setSingCtaInfo={setSingCtaInfo}
        lyricsOpen={lyricsOpen} setLyricsOpen={setLyricsOpen}
        songOpen={songOpen} setSongOpen={setSongOpen} songSeed={songSeed}
        voiceOpen={voiceOpen} setVoiceOpen={setVoiceOpen}
        libraryOpen={libraryOpen} setLibraryOpen={setLibraryOpen}
        midiOpen={midiOpen} setMidiOpen={setMidiOpen}
        armedChannels={armedChannels}
        onChannelArm={(idx, armed) => {
          setArmedChannels((prev) => {
            const next = new Set(prev);
            if (armed) next.add(idx);
            else next.delete(idx);
            return next;
          });
          toast.message(armed ? `Armed CH ${padCh(idx)} via MIDI` : `Disarmed CH ${padCh(idx)} via MIDI`, {
            duration: 1400,
          });
        }}
        drumOpen={drumOpen} setDrumOpen={setDrumOpen}
        bassOpen={bassOpen} setBassOpen={setBassOpen}
        jsfxOpen={jsfxOpen} setJsfxOpen={setJsfxOpen}
        backingVocalsOpen={backingVocalsOpen} setBackingVocalsOpen={setBackingVocalsOpen}
        clipOpen={clipOpen} setClipOpen={setClipOpen}
        mtEditorOpen={mtEditorOpen} setMtEditorOpen={setMtEditorOpen}
        loopsOpen={loopsOpen} setLoopsOpen={setLoopsOpen}
        jamWizardOpen={jamWizardOpen} setJamWizardOpen={setJamWizardOpen}
        sunoOpen={sunoOpen} setSunoOpen={setSunoOpen} sunoSeed={sunoSeed}
        loopStationOpen={loopStationOpen} setLoopStationOpen={setLoopStationOpen}
        recordingsOpen={recordingsOpen} setRecordingsOpen={setRecordingsOpen}
        shareSessionOpen={shareSessionOpen} setShareSessionOpen={setShareSessionOpen}
        tempo={tempo}
        musicalKey={musicalKey}
      />
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        actions={{
          openSuno:       () => setSunoOpen(true),
          openSong:       () => setSongOpen(true),
          openLyrics:     () => setLyricsOpen(true),
          openVoice:      () => setVoiceOpen(true),
          openDrum:       () => setDrumOpen(true),
          openBass:       () => setBassOpen(true),
          openLoops:      () => setLoopsOpen(true),
          openJam:        () => setJamWizardOpen(true),
          openMtEditor:   () => setMtEditorOpen(true),
          openLibrary:    () => setLibraryOpen(true),
          openRecordings: () => setRecordingsOpen(true),
          openMidi:       () => setMidiOpen(true),
          openMidiClip:   () => setMidiClipOpen(true),
          openArrangement: () => setArrangementOpen(true),
          openExplore:    () => { window.open("/explore", "_blank", "noopener,noreferrer"); },
          openAdmin:      () => { window.open("/admin", "_blank", "noopener,noreferrer"); },
          openStatus:     () => { window.open("/status", "_blank", "noopener,noreferrer"); },
          openShare:      () => setShareSessionOpen(true),
          openCollab:     () => setCollabOpen(true),
          openUpload:     handleHeaderUploadClick,
          togglePlay:     handlePlay,
          stop:           handleStop,
          toggleLoop:     handleToggleLoop,
          toggleMetronome: handleToggleMetronome,
          toggleDryMode:  handleToggleDryMode,
          saveSession:    handleSave,
          loadSession:    handleLoadSession,
          exportWav:      handleExport,
        }}
      />
      <CollabModal
        open={collabOpen}
        onClose={() => setCollabOpen(false)}
        onOpen={() => setCollabOpen(true)}
        roomId={collabRoomId}
        userName={collabUserName}
        status={collab.status}
        peers={collab.peers}
        error={collab.error}
        mesh={mesh}
        crdt={{ ready: yjs.ready, sync: yjs.sync, stats: yjs.stats, openChannels: mesh.openChannelCount }}
        onJoin={(rid, name) => {
          setCollabRoomId(rid);
          setCollabUserName(name);
          try { localStorage.setItem("elpm-collab-name", name); } catch (e) { void e; }
          toast.success(`Joining room…`, { description: rid });
        }}
        onLeave={() => {
          setCollabRoomId(null);
          collab.disconnect();
          toast.message("Left collab room");
        }}
        onChangeName={(n) => setCollabUserName(n)}
      />
      <SingOverItCTA
        open={!!singCtaInfo && !voiceOpen}
        jamInfo={singCtaInfo}
        onOpen={() => {
          setSingCtaInfo(null);
          setVoiceOpen(true);
        }}
        onDismiss={() => setSingCtaInfo(null)}
      />
      <MidiClipEditor
        open={midiClipOpen}
        onClose={() => setMidiClipOpen(false)}
        engine={engine}
        onClipRendered={() => setMidiClipOpen(false)}
      />
      <ArrangementView
        open={arrangementOpen}
        onClose={() => setArrangementOpen(false)}
        engine={engine}
        tempo={tempo}
      />
    </div>
  );
}
